import {
  useCallback, useMemo, useRef, useState,
} from 'react'

// Distributes items of known aspect ratio into the shortest column so each column
// ends at about the same height. Greedy from the first item, so appending a page
// never moves an item already on screen. Column heights are tracked in column
// widths (1 / aspectRatio + the gap), which is all a fixed-width column needs.
export const distributeIntoColumns = (items, columnCount, gapRatio = 0) => {
  const columns = Array.from({ length: columnCount }, () => [])
  const heights = new Array(columnCount).fill(0)

  items.forEach((item) => {
    const shortest = heights.indexOf(Math.min(...heights))
    columns[shortest].push(item)
    heights[shortest] += 1 / item.aspectRatio + gapRatio
  })

  return columns
}

// Never more columns than `minColumnWidth` allows: the hover actions (two 24px
// buttons plus the Generate 3D pill) need the full width of an image, so the row
// drops to a single column rather than squeezing images below that floor.
export const getColumnCount = (width, { minColumnWidth, gap }) => {
  if (width <= 0) return 1
  return Math.max(1, Math.floor((width + gap) / (minColumnWidth + gap)))
}

// Returns a [callbackRef, columns] pair. A callback ref (not a ref object plus an
// effect) because the container only mounts once the first page has loaded, and
// an effect keyed on a ref object would fire once with `.current` null and never
// re-run. Measurement happens on mount and on every container resize.
function useMasonryColumns(items, { minColumnWidth, gap }) {
  const [columnCount, setColumnCount] = useState(1)
  const [columnWidth, setColumnWidth] = useState(minColumnWidth)
  const observerRef = useRef(null)

  const measure = useCallback((node) => {
    const width = node.getBoundingClientRect().width
    const count = getColumnCount(width, { minColumnWidth, gap })
    setColumnCount(count)
    setColumnWidth((width - gap * (count - 1)) / count)
  }, [minColumnWidth, gap])

  const containerRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (!node || typeof ResizeObserver === 'undefined') return

    measure(node)
    observerRef.current = new ResizeObserver(() => measure(node))
    observerRef.current.observe(node)
  }, [measure])

  const columns = useMemo(
    () => distributeIntoColumns(items, columnCount, columnWidth > 0 ? gap / columnWidth : 0),
    [items, columnCount, columnWidth, gap],
  )

  return [containerRef, columns]
}

export default useMasonryColumns
